import { FormikImageUpload } from 'components/v2/images-upload/FormikImageUpload';
import SmartLabel from 'components/v2/labels/SmartLabel';
import { Form, Formik } from 'formik';
import { Dialog } from 'primereact/dialog';
import { useRef } from 'react';
import styled from 'styled-components';
import { CollocationCenterModel, Option } from 'types/types';
import * as Yup from 'yup';

import { StyledReactSelect } from '../../../../styles/Styles.styled';
import { DialogClearStylesButtonsFooter, DialogFooter } from 'components/accounts/DialogClearStyles';
import { PrimaryButton, SecondaryButton } from 'components/v2/buttons';
import InputFormField from 'components/v2/form/InputFormField';
import { editAdminSelectStyles } from 'components/form/EditSelectStyles';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Co-location centre name is required'),
  countries: Yup.array().min(1, 'Required'),
  logo: Yup.mixed()
    .required('Logo is required')
    .test('is-valid-logo', 'Logo is required', (value) => {
      if (!value) return false;
      if (typeof value === 'string') return value.trim() !== '';
      if (value instanceof File) return value.size > 0;
      return false;
    }),
});

type TProps = {
  show: boolean;
  onHide: () => void;
  onSubmit: (item: CollocationCenterModel) => void;
  item?: CollocationCenterModel;
  countriesLookup: Option[];
};

const EditCollocationCenterDialog = ({ show, onHide, onSubmit, item, countriesLookup }: TProps) => {
  const formRef = useRef<any>();

  const initialValues: any = item
    ? {
      name: item.name,
      description: item.description,
      countryCodes: item.countryCodes,
      countries: item.countryCodes
        ?.map((code) => countriesLookup.find((item) => item.value === code))
        .filter((item): item is { value: string; label: string } => !!item),
      logotypeUrl: item.logotypeUrl,
      logo: item.logotypeUrl,
    }
    : {
      name: '',
      description: '',
      countries: [],
      localHomepage: '',
      logotypeUrl: '',
      logo: null,
    };

  return (
    <Dialog
      header="Edit co-location centre"
      visible={show}
      style={{ maxWidth: '33rem', width: '33rem', minWidth: '20rem' }}
      onHide={onHide}
    >
      <Formik
        innerRef={formRef}
        enableReinitialize
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {({ values, isValid, dirty, isSubmitting }) => (
          <Form>
            <Column>
              <FieldGroup>
                <InputFormField
                  required
                  label="Co-location centre name"
                  name="name"
                  placeholder="Name"
                  errorPositioning='none' />
              </FieldGroup>

              <FieldGroup>
                <InputFormField
                  required
                  label="Description"
                  name="description"
                  placeholder="Description"
                  errorPositioning='none' />
              </FieldGroup>

              <FieldGroup>
                <SmartLabel label="Countries" required={(values as any)?.countries?.length === 0} />
                <StyledReactSelect
                  styles={editAdminSelectStyles}
                  name={'countries'}
                  className={'w-full'}
                  options={countriesLookup}
                  isMulti
                  isClearable
                  placeholder="Pick countries in the co-location centre"
                  supressErrors
                />
              </FieldGroup>
              <FieldGroup>
                <SmartLabel
                  label="Co-location centre logo"
                  required={!(values as any)?.logo}
                  tooltip="Logo will be used in the EPDs issued by this CLC. It will be displayed on the cover page of the EPD document."
                />
                <FormikImageUpload name="logo" hideRequiredError />
              </FieldGroup>
            </Column>
            <DialogFooter style={{ paddingBottom: '2rem' }}>
              <DialogClearStylesButtonsFooter>
                <SecondaryButton
                  type="button"
                  label="Cancel"
                  onClick={onHide}
                />
                <PrimaryButton
                  type="submit"
                  label="Save"
                  disabled={!isValid || !dirty || isSubmitting}
                />
              </DialogClearStylesButtonsFooter>
            </DialogFooter>
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

const Column = styled.div`
  padding: 0 2rem;
  display: grid;
  grid-column: auto;
  row-gap: 1rem;
  box-sizing: border-box;
  margin: 1rem 0;
`;

const FieldGroup = styled.div`
  width: 100%;
  margin: 0;
`;

export default EditCollocationCenterDialog;
